import { docReady } from '../utils/docReady';
const selectors = {
    header: '[data-component="header"]',
    item: '[data-mega-menu-item]',
    trigger: '[data-mega-menu-trigger]',
    panel: '[data-mega-menu-panel]',
    overlay: '[data-mega-menu-overlay]',
    shell: '[data-mega-menu-shell]',
    block: '[data-mega-menu-block]',
};
// Delays stop the menu flickering open/closed as the cursor crosses the nav
const OPEN_DELAY = 80;
const CLOSE_DELAY = 200;
// Keep in sync with $mega-menu-duration in _mega-menu.scss
const TRANSITION_DURATION = 300;
let header;
let overlay;
let shell;
let resizeTimer;
let openItem = null;
let openTimer;
let closeTimer;
let lastPointerType = '';
let ignoreFocus = false;
const getTrigger = (item) => item.querySelector(selectors.trigger);
const getPanel = (item) => item.querySelector(selectors.panel);
// Runs a class/style change with transitions off, so it applies instantly
const withoutTransition = (elements, change) => {
    elements.forEach((element) => element.classList.add('no-transition'));
    change();
    // Force a reflow so the instant change is painted before transitions come back on
    void shell.offsetHeight;
    elements.forEach((element) => element.classList.remove('no-transition'));
};
// Fades everything out together: the shell, the open panel and the overlay
const close = () => {
    var _a, _b;
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    if (!openItem)
        return;
    (_a = getTrigger(openItem)) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'false');
    (_b = getPanel(openItem)) === null || _b === void 0 ? void 0 : _b.classList.remove('is-open');
    shell === null || shell === void 0 ? void 0 : shell.classList.remove('is-visible');
    header.classList.remove('header--mega-active');
    overlay === null || overlay === void 0 ? void 0 : overlay.classList.remove('is-visible');
    openItem = null;
};
// Switching items: swap the content instantly and animate only the height,
// from the old panel's height to the new one's
const switchTo = (fromPanel, toPanel) => {
    const fromHeight = shell.offsetHeight;
    withoutTransition([fromPanel, toPanel, shell], () => {
        fromPanel.classList.remove('is-open', 'is-resizing');
        fromPanel.style.height = '';
        toPanel.style.height = '';
        toPanel.classList.add('is-open');
    });
    const toHeight = toPanel.offsetHeight;
    if (toHeight === fromHeight)
        return;
    withoutTransition([toPanel, shell], () => {
        toPanel.style.height = `${fromHeight}px`;
        shell.style.height = `${fromHeight}px`;
    });
    toPanel.classList.add('is-resizing');
    toPanel.style.height = `${toHeight}px`;
    shell.style.height = `${toHeight}px`;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
        toPanel.classList.remove('is-resizing');
        toPanel.style.height = '';
    }, TRANSITION_DURATION);
};
const open = (item) => {
    var _a, _b;
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    if (openItem === item)
        return;
    const panel = getPanel(item);
    if (!panel || !shell)
        return;
    // Caps the panel height to the viewport below the header
    header.style.setProperty('--mega-menu-top', `${header.getBoundingClientRect().bottom}px`);
    const fromPanel = openItem ? getPanel(openItem) : null;
    if (openItem)
        (_a = getTrigger(openItem)) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'false');
    (_b = getTrigger(item)) === null || _b === void 0 ? void 0 : _b.setAttribute('aria-expanded', 'true');
    if (fromPanel) {
        switchTo(fromPanel, panel);
    }
    else {
        // First open: size the shell to the panel instantly, then fade both in
        window.clearTimeout(resizeTimer);
        panel.classList.remove('is-resizing');
        panel.style.height = '';
        withoutTransition([shell], () => {
            shell.style.height = `${panel.offsetHeight}px`;
        });
        panel.classList.add('is-open');
        shell.classList.add('is-visible');
    }
    header.classList.add('header--mega-active');
    overlay === null || overlay === void 0 ? void 0 : overlay.classList.add('is-visible');
    openItem = item;
};
const scheduleOpen = (item) => {
    window.clearTimeout(closeTimer);
    window.clearTimeout(openTimer);
    // Switch straight away when moving between items with a menu already open
    if (openItem) {
        open(item);
        return;
    }
    openTimer = window.setTimeout(() => open(item), OPEN_DELAY);
};
const scheduleClose = () => {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(close, CLOSE_DELAY);
};
const bindItem = (item) => {
    const trigger = getTrigger(item);
    if (!trigger) {
        // Plain links still close an open menu when hovered
        item.addEventListener('mouseenter', scheduleClose);
        return;
    }
    item.addEventListener('mouseenter', () => scheduleOpen(item));
    item.addEventListener('mouseleave', scheduleClose);
    item.addEventListener('focusin', () => {
        if (!ignoreFocus)
            open(item);
    });
    item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget))
            close();
    });
    trigger.addEventListener('pointerdown', (event) => {
        lastPointerType = event.pointerType;
    });
    // Touch has no hover: the first tap opens the menu, a second tap follows the link
    trigger.addEventListener('click', (event) => {
        if (lastPointerType === 'touch' && openItem !== item) {
            event.preventDefault();
            open(item);
        }
    });
};
const init = () => {
    header = document.querySelector(selectors.header);
    overlay = document.querySelector(selectors.overlay);
    shell = document.querySelector(selectors.shell);
    openItem = null;
    if (!header)
        return;
    header.querySelectorAll(selectors.item).forEach(bindItem);
    overlay === null || overlay === void 0 ? void 0 : overlay.addEventListener('click', close);
};
document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openItem)
        return;
    const trigger = getTrigger(openItem);
    close();
    // Returning focus to the trigger shouldn't reopen the menu just closed
    ignoreFocus = true;
    trigger === null || trigger === void 0 ? void 0 : trigger.focus();
    ignoreFocus = false;
});
// Theme editor: show the dropdown while its item or column block is selected
document.addEventListener('shopify:block:select', (event) => {
    const block = document.querySelector(`${selectors.block}[data-mega-menu-block="${event.detail.blockId}"]`);
    const item = block === null || block === void 0 ? void 0 : block.closest(selectors.item);
    if (item)
        open(item);
});
document.addEventListener('shopify:block:deselect', close);
document.addEventListener('shopify:section:load', init);
docReady(init);
